import { useEffect, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight, LoaderCircle, Megaphone } from 'lucide-react'
import { PageShell } from '@/features/public-site/page-shell'
import { appFetch } from '@/shared/api/app-client'
import type { ApiResponse } from '@/shared/api/app-types'

export const Route = createFileRoute('/announcements/')({ component: AnnouncementsPage })

type PublicAnnouncement = {
  id: number
  title: string
  body: string
  excerpt: string
  coverImageUrl: string | null
  category: string
  authorName: string
  publishedAt: string
}

const fallbackCovers = ['/assets/landing/ting.png', '/assets/landing/projects.png', '/assets/landing/timeline.png', '/assets/landing/export.png']

function coverFor(item: PublicAnnouncement, index: number) {
  return item.coverImageUrl || fallbackCovers[index % fallbackCovers.length]
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' })
}

function readMinutes(text: string) {
  return Math.max(2, Math.ceil(text.replace(/\s/g, '').length / 300))
}

function AnnouncementsPage() {
  const [items, setItems] = useState<PublicAnnouncement[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    void appFetch<ApiResponse<{ items: PublicAnnouncement[] }>>('/api/announcements?limit=100')
      .then((response) => setItems(response.data.items))
      .catch((requestError) => setError(requestError instanceof Error ? requestError.message : '内容加载失败，请稍后重试。'))
      .finally(() => setLoading(false))
  }, [])

  const featured = items[0]
  const more = items.slice(1)

  return (
    <PageShell>
      <div className="min-h-[680px] bg-[#f6f9fc]">
        <header className="mx-auto max-w-[1180px] px-5 pb-12 pt-16 text-center sm:px-8 sm:pb-16 sm:pt-20">
          <p className="text-xs font-bold tracking-[.15em] text-[#3f9437]">JIAN BLOG</p>
          <h1 className="mt-5 text-4xl font-semibold tracking-[-.045em] text-[#101727] sm:text-6xl">创作洞察与产品更新</h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-[#697184] sm:text-lg">关于 AI 视频生产、内容自动化、多平台交付和 JIAN 产品进展的实践文章。</p>
        </header>

        <main className="mx-auto max-w-[1180px] px-5 pb-20 sm:px-8">
          {loading ? <div className="flex items-center justify-center gap-2 py-24 text-sm text-[#697184]"><LoaderCircle className="size-4 animate-spin" />正在加载文章…</div> : null}
          {error ? <div className="rounded-xl border border-red-200 bg-white px-5 py-4 text-sm text-red-700">{error}</div> : null}
          {!loading && !error && !items.length ? <div className="rounded-2xl border border-[#e1e7de] bg-white py-24 text-center"><Megaphone className="mx-auto size-6 text-[#82907f]" /><h2 className="mt-4 text-lg font-semibold text-[#273247]">暂无公开文章</h2><p className="mt-2 text-sm text-[#697184]">产品动态和创作指南会在这里发布。</p></div> : null}

          {!loading && !error && featured ? (
            <>
              <Link to="/announcements/$announcementId" params={{ announcementId: String(featured.id) }} className="group grid overflow-hidden rounded-[22px] border border-[#dfe5dc] bg-white shadow-[0_18px_50px_rgba(27,45,30,0.06)] lg:grid-cols-[1.05fr_.95fr]">
                <div className="overflow-hidden bg-[#eef2ed]"><img src={coverFor(featured, 0)} alt={featured.title} className="aspect-[16/10] h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]" /></div>
                <div className="flex flex-col justify-center p-7 sm:p-10 lg:p-12">
                  <p className="text-xs font-bold tracking-[.12em] text-[#3f9437]">{featured.category}</p>
                  <h2 className="mt-4 text-3xl font-semibold leading-tight tracking-[-.035em] text-[#101727] sm:text-4xl">{featured.title}</h2>
                  <p className="mt-5 line-clamp-4 text-[15px] leading-7 text-[#596274] sm:text-base">{featured.excerpt}</p>
                  <div className="mt-7 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#7b8493]"><span className="font-semibold text-[#3d4658]">{featured.authorName}</span><span aria-hidden="true">·</span><time dateTime={featured.publishedAt}>{formatDate(featured.publishedAt)}</time><span aria-hidden="true">·</span><span>{readMinutes(featured.excerpt)} 分钟阅读</span></div>
                  <span className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#3f9437]">阅读文章 <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" /></span>
                </div>
              </Link>

              {more.length ? <section className="mt-14"><div className="mb-7 flex items-end justify-between gap-4"><div><p className="text-xs font-bold tracking-[.12em] text-[#3f9437]">MORE STORIES</p><h2 className="mt-2 text-2xl font-semibold tracking-[-.025em] text-[#101727] sm:text-3xl">更多文章</h2></div><span className="text-sm text-[#7b8493]">共 {items.length} 篇</span></div><div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{more.map((item, index) => <Link key={item.id} to="/announcements/$announcementId" params={{ announcementId: String(item.id) }} className="group overflow-hidden rounded-2xl border border-[#dfe5dc] bg-white transition duration-200 hover:-translate-y-1 hover:shadow-[0_16px_38px_rgba(27,45,30,0.08)]"><div className="overflow-hidden bg-[#eef2ed]"><img src={coverFor(item, index + 1)} alt={item.title} className="aspect-[16/10] w-full object-cover transition duration-500 group-hover:scale-[1.025]" /></div><div className="p-6"><p className="text-[11px] font-bold tracking-[.12em] text-[#3f9437]">{item.category}</p><h3 className="mt-3 line-clamp-2 text-xl font-semibold leading-7 tracking-[-.02em] text-[#101727]">{item.title}</h3><p className="mt-3 line-clamp-3 text-sm leading-6 text-[#697184]">{item.excerpt}</p><div className="mt-6 flex items-center justify-between gap-3 text-xs text-[#8a929f]"><span>{item.authorName}</span><time dateTime={item.publishedAt}>{formatDate(item.publishedAt)}</time></div></div></Link>)}</div></section> : null}
            </>
          ) : null}
        </main>
      </div>
    </PageShell>
  )
}
